import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { MdVisibility, MdTouchApp, MdInventory2, MdOpenInNew } from 'react-icons/md';

import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StatCard from '../../../shared/layout/cards/StatCard';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import EmptyState from '../../../shared/layout/feedback/EmptyState';
import { SimpleSelect } from '../../../shared/layout/forms/AppSelect';
import { fetchDiscoveryStats } from '../../../services/Http/Api';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

/**
 * Engagement analytics for discounts and catalog items.
 * Impressions, detail opens and outbound clicks are kept distinct.
 */
const RANGES = [
    { days: 7, granularity: 'day' },
    { days: 30, granularity: 'day' },
    { days: 90, granularity: 'week' },
    { days: 365, granularity: 'month' },
];

const DiscoveryStats = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    const [rangeIndex, setRangeIndex] = useState(1);
    const [granularity, setGranularity] = useState(RANGES[1].granularity);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const range = RANGES[rangeIndex];

    const load = useCallback(() => {
        let cancelled = false;
        setLoading(true);

        fetchDiscoveryStats({ days: range.days, granularity })
            .then((response) => {
                if (cancelled) return;
                setData(response);
                setError(null);
            })
            .catch(() => {
                if (!cancelled) setError(t('Stats.loadFailed'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [range.days, granularity, t]);

    useEffect(load, [load]);

    // Promjena raspona povlaci i razumnu granularnost, ali je korisnik i dalje
    // moze rucno promijeniti.
    const changeRange = (index) => {
        setRangeIndex(index);
        setGranularity(RANGES[index].granularity);
    };

    const totals = data?.totals || {};
    const catalog = data?.catalog || {};
    const hasEngagementSeries = Array.isArray(data?.engagement_over_time);
    const rows = data?.engagement_over_time || [];

    const buckets = [...new Set(rows.map((row) => row.bucket))].sort();
    const seriesFor = (metric) => buckets.map((bucket) => {
        const row = rows.find((r) => r.bucket === bucket);
        return row ? Number(row[metric]) : 0;
    });

    const formatNumber = (value) => Number(value || 0).toLocaleString('hr-HR');
    const labelFor = (bucket) => {
        const date = new Date(bucket);
        if (Number.isNaN(date.getTime())) return bucket;
        if (granularity === 'month') return date.toLocaleDateString('hr-HR', { month: 'short', year: '2-digit' });
        if (granularity === 'week') return date.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' });
        return date.toLocaleDateString('hr-HR', { day: '2-digit', month: '2-digit' });
    };

    const chartOptions = {
        chart: {
            id: 'discovery-views',
            type: 'area',
            toolbar: { show: false },
            fontFamily: 'inherit',
            zoom: { enabled: false },
            animations: { enabled: true, speed: 350 },
        },
        colors: ['#821692', '#177e89', '#eb6834'],
        stroke: { width: 3, curve: 'smooth' },
        markers: { size: 0, hover: { size: 5 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', horizontalAlign: 'left', fontSize: '13px', markers: { radius: 3 } },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 0.2, opacityFrom: 0.2, opacityTo: 0.015, stops: [0, 90, 100] },
        },
        grid: { borderColor: theme.colors.border || '#edf0f5', strokeDashArray: 4, padding: { left: 8, right: 12 } },
        xaxis: {
            categories: buckets.map(labelFor),
            axisBorder: { show: false },
            axisTicks: { show: false },
            tooltip: { enabled: false },
            labels: { rotate: -25, hideOverlappingLabels: true, style: { colors: theme.colors.textMuted || '#7b8495' } },
            crosshairs: { stroke: { color: '#a78bfa', dashArray: 4 } },
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: {
                formatter: (value) => formatNumber(Math.round(value)),
                style: { colors: theme.colors.textMuted || '#7b8495' },
            },
        },
        tooltip: { shared: true, intersect: false, y: { formatter: (value) => formatNumber(value) } },
    };

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle>{t('Stats.title')}</PageTitle>

                <Filters>
                    <SimpleSelect
                        value={String(rangeIndex)}
                        onChange={(event) => changeRange(Number(event.target.value))}
                        aria-label={t('Stats.rangeLabel')}
                    >
                        {RANGES.map((option, index) => (
                            <option key={option.days} value={index}>{t(`Stats.range_${option.days}`)}</option>
                        ))}
                    </SimpleSelect>

                    <SimpleSelect
                        value={granularity}
                        onChange={(event) => setGranularity(event.target.value)}
                        aria-label={t('Stats.granularityLabel')}
                    >
                        <option value="day">{t('Stats.byDay')}</option>
                        <option value="week">{t('Stats.byWeek')}</option>
                        <option value="month">{t('Stats.byMonth')}</option>
                    </SimpleSelect>
                </Filters>
            </PageHeader>

            <PageContent>
                {error && <ErrorMessage>{error}</ErrorMessage>}

                {loading && !data ? <LoadingMessage>{t('Stats.loading')}</LoadingMessage> : null}

                {data && (
                    <>
                        <MetricsGrid>
                            <StatCard icon={<MdVisibility />} title={t('Stats.discountImpressions')} value={formatNumber(totals.impressions ?? totals.viewed)} color="campaigns" />
                            <StatCard icon={<MdOpenInNew />} title={t('Stats.discountDetailViews')} value={formatNumber(totals.detail_views)} color="info" />
                            <StatCard icon={<MdTouchApp />} title={t('Stats.discountClicks')} value={formatNumber(totals.clicks)} color="transactions" />
                            <StatCard
                                icon={<MdInventory2 />}
                                title={t('Stats.catalogImpressions')}
                                value={formatNumber(catalog.impressions)}
                                color="campaigns"
                            />
                            <StatCard
                                icon={<MdInventory2 />}
                                title={t('Stats.catalogDetailViews')}
                                value={formatNumber(catalog.views)}
                                color="info"
                            />
                            <StatCard
                                icon={<MdOpenInNew />}
                                title={t('Stats.catalogClicks')}
                                value={formatNumber(catalog.clicks)}
                                color="transactions"
                            />
                        </MetricsGrid>

                        <ChartsGrid>
                            <Block>
                                <BlockHeading>{t('Stats.chartDiscountTitle')}</BlockHeading>
                                {!hasEngagementSeries ? (
                                    <EmptyState>{t('Stats.backendUpdateRequired')}</EmptyState>
                                ) : buckets.length === 0 ? (
                                    <EmptyState>{t('Stats.noData')}</EmptyState>
                                ) : (
                                    <Suspense fallback={<LoadingMessage>{t('Stats.loading')}</LoadingMessage>}>
                                        <ReactApexChart
                                            type="area"
                                            height={320}
                                            options={{ ...chartOptions, chart: { ...chartOptions.chart, id: 'discount-engagement' } }}
                                            series={[
                                                { name: t('Stats.discountImpressions'), data: seriesFor('discount_impressions') },
                                                { name: t('Stats.discountDetailViews'), data: seriesFor('discount_detail_views') },
                                                { name: t('Stats.discountClicks'), data: seriesFor('discount_clicks') },
                                            ]}
                                        />
                                    </Suspense>
                                )}
                            </Block>
                            <Block>
                                <BlockHeading>{t('Stats.chartCatalogTitle')}</BlockHeading>
                                {!hasEngagementSeries ? (
                                    <EmptyState>{t('Stats.backendUpdateRequired')}</EmptyState>
                                ) : buckets.length === 0 ? (
                                    <EmptyState>{t('Stats.noData')}</EmptyState>
                                ) : (
                                    <Suspense fallback={<LoadingMessage>{t('Stats.loading')}</LoadingMessage>}>
                                        <ReactApexChart
                                            type="area"
                                            height={320}
                                            options={{ ...chartOptions, chart: { ...chartOptions.chart, id: 'catalog-engagement' } }}
                                            series={[
                                                { name: t('Stats.catalogImpressions'), data: seriesFor('catalog_impressions') },
                                                { name: t('Stats.catalogDetailViews'), data: seriesFor('catalog_detail_views') },
                                                { name: t('Stats.catalogClicks'), data: seriesFor('catalog_clicks') },
                                            ]}
                                        />
                                    </Suspense>
                                )}
                            </Block>
                        </ChartsGrid>

                        <BlockNote>{t('Stats.chartNote')}</BlockNote>

                        <Block>
                            <BlockHeading>{t('Stats.topItems')}</BlockHeading>

                            {(data.top_items || []).length === 0 ? (
                                <EmptyState>{t('Stats.noData')}</EmptyState>
                            ) : (
                                <TableScroll>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th>{t('Stats.product')}</th>
                                                <th>{t('Stats.impressions')}</th>
                                                <th>{t('Stats.detailViews')}</th>
                                                <th>{t('Stats.clicks')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.top_items.map((item) => {
                                                const impressions = Number(item.impressions ?? item.viewed) || 0;
                                                const detailViews = Number(item.detail_views) || 0;
                                                const clicks = Number(item.clicks) || 0;

                                                return (
                                                    <tr key={item.item_discount_id}>
                                                        <td>{item.product_name || '—'}</td>
                                                        <td>{formatNumber(impressions)}</td>
                                                        <td>{formatNumber(detailViews)}</td>
                                                        <td>{formatNumber(clicks)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </TableScroll>
                            )}
                        </Block>
                    </>
                )}
            </PageContent>
        </PageContainer>
    );
};

const Filters = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;

    > * {
        min-width: 170px;
    }
`;

const MetricsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1rem;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 560px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const Block = styled(StyledCard)`
    margin-top: 1.25rem;
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.25rem;

    ${Block} {
        min-width: 0;
    }

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
        gap: 0;
    }
`;

const BlockHeading = styled.h3`
    margin: 0 0 1rem;
    font-size: 1.05rem;
    color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
`;

const BlockNote = styled.p`
    margin: 0.75rem 0 0;
    font-size: 0.78rem;
    color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
`;

const TableScroll = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        text-align: left;
        padding: 0.55rem 0.5rem;
        border-bottom: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
        font-size: 0.88rem;
        white-space: nowrap;
    }

    th {
        font-weight: 600;
        color: ${({ theme }) => theme.colors.textMuted || '#6b7280'};
    }

    td:first-child {
        white-space: normal;
        min-width: 220px;
    }

    td:not(:first-child), th:not(:first-child) {
        text-align: right;
        width: 110px;
    }
`;

export default DiscoveryStats;
